<template>
  <language-button @change-language="getBasePage"></language-button>
  <navi-button mode="middle"></navi-button>
  <!-- METADATA : START -->
  <base-social
    mode="social-finder"
    :share-url="shareUrl"
    :icon-class="iconClass"
    :button-class="buttonClass"
  >
  </base-social>
  <!-- METADATA: END -->
  <!-- BASE LAYOUT FOR FINDER: (1) LOGO, (2) PAGENAME, (3) HEADER, (4) SELECT, (5) RESULTS -->
  <base-finder class="rocks">
    <!-- BASE LAYOUT: 1 - LOGO -->
    <template v-slot:logo>
      <base-logo mode="rocks__logo"></base-logo>
    </template>
    <!-- BASE LAYOUT: 2 - PAGENAME -->
    <template v-slot:pageName>{{ getPageTitle }}</template>
    <!-- BASE LAYOUT: 3 - HEADER -->
    <template v-slot:header>
      <base-finderHeader
        @inputQuery="listenToQuery"
        :allSuggestions="getSuggestions"
        @submitQuery="listenToSpecificQuery"
      >
      </base-finderHeader>
    </template>
    <!-- BASE LAYOUT: 4 - SELECT -->
    <template v-slot:select>
      <button
        class="rockBtn"
        v-for="sbtn in selectBtn[getCurrentLanguage]"
        :key="sbtn"
        @click="getRockType(sbtn)"
      >
        {{ sbtn }}
      </button>
    </template>
    <!-- BASE LAYOUT: 5 - RESULTS -->
    <template v-slot:results>
      <RouterView />
      <!-- <div class="loadingStatus " :class="active"></div> -->
      <div class="loadingStatus"></div>

      <!-- ACTION BUTTONS - START -->

      <action-button @click="listenToRefresh" mode="refresh">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm5 20l-1.359-2.038c-1.061.653-2.305 1.038-3.641 1.038-3.859 0-7-3.14-7-7h2c0 2.757 2.243 5 5 5 .927 0 1.786-.264 2.527-.708l-1.527-2.292h5.719l-1.719 6zm0-8c0-2.757-2.243-5-5-5-.927 0-1.786.264-2.527.708l1.527 2.292h-5.719l1.719-6 1.359 2.038c1.061-.653 2.305-1.038 3.641-1.038 3.859 0 7 3.14 7 7h-2z"
          />
        </svg>
      </action-button>

      <action-button @click="goUp" mode="goUp">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="icon-upArrow"
          viewBox="0 0 24 24"
        >
          <path
            d="M24 12c0-6.627-5.373-12-12-12s-12 5.373-12 12 5.373 12 12 12 12-5.373 12-12zm-17 1h4v-8h2v8h4l-5 6-5-6z"
          />
        </svg>
      </action-button>
      <!-- ACTION BUTTONS: END -->
    </template>
  </base-finder>
  <!-- END OF BASE LAYOUT: FINDER -->
</template>

<script setup>
import { computed, onBeforeMount, ref } from "vue";
import { useStore } from "vuex";
import { useSeoMeta, useHead } from "@unhead/vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

const selectBtn = ref({
  en: ["igneous", "metamorphic", "sedimentary"],
  pt: ["ígnea", "metamórfica", "sedimentar"],
});

/** social network buttons */
const shareUrl = ref({
  title: "lupanalua",
  text: "Rock database searcheable by name or type",
  url: "https://www.lupanalua.com/rocks",
});

const buttonClass = {
  basic: "social--action-finder",
};
const iconClass = {
  basic: "icon--finder-basic",
};
/** SEO */
useSeoMeta({
  title: "Search for rocks | lupanalua",
  description:
    "Rock database searcheable by name or type: igneous, metamorphic, or sedimentary. From this page you can access a rock fact sheet, with details on grain size, minerals, and origin. lupanalua is a website about environmental education and earth sciences.",
  author: "lupanalua by Lu Shibata",
});

useHead({
  link: [
    {
      rel: "canonical",
      href: "https://lupanalua.com/rocks",
    },
  ],
});

onBeforeMount(() => {
  getBasePage();
});

const store = useStore();

/**
 * @returns the whole page in the user language
 */
async function getBasePage() {
  store.dispatch("rocks/getPageTitle");
  await store.dispatch("rocks/getRockNames");
  if (route.path === "/rocks") {
    store.dispatch("rocks/getAllRocks");
  } else {
    const one = store.getters["rocks/getOneRock"].idName;
    await store.dispatch("rocks/getRockFromParams", one);
  }
}

/**
 * @returns {String} of the page name in the user language
 */

const getPageTitle = computed(() => {
  return store.getters["rocks/getPageTitle"];
});

/**
 * @param {String} q is the letter typed by user
 */
function listenToQuery(q) {
  store.dispatch("rocks/getQuerySuggestions", q);
}
/**
 * @param {String} sq is the suggested mineral name that the user clicked
 */
function listenToSpecificQuery(sq) {
  document.querySelector("#app").style.scale = 1.0;
  store.dispatch("rocks/getOneRock", sq);
}
/**
 * @returns {Array} of mineral names in the user language, according to query
 */
const getSuggestions = computed(() => {
  return store.getters["rocks/getQuerySuggestions"];
});

/** Methods **/
/**
 * action button to go up
 */
function goUp() {
  window.scrollTo(0, 0);
}
/**
 * action button to reset rendering all minerals
 */
function listenToRefresh() {
  store.dispatch("rocks/getAllRocks");
  router.push("/rocks");
}

/**
 * @returns {String} of the user language
 */
const getCurrentLanguage = computed(() => {
  return store.getters.getCurrentLanguage;
});
/**
 * returns rock by clicked type
 */
function getRockType(rockType) {
  store.dispatch("rocks/getRockType", rockType);
}
</script>

<style scoped>
.icon-upArrow {
  transform: rotate(180deg);
}

.rockBtn {
  background: linear-gradient(to bottom, #edf2ff, #edf2ff);
  box-shadow: 3px 3px 2px 1px var(--color-grey-3);
  border: none;
  border-radius: 30px;
  font-family: var(--font-display);
  text-transform: uppercase;
  width: 20rem;
  height: 80%;
  color: var(--color-grey-4);
  font-size: 1.6rem;
  text-decoration: none;
  margin-right: 2rem;
  transition: ease-out 2s;
}

.rockBtn:hover {
  background: linear-gradient(white, white);
}
.loadingStatus {
  visibility: hidden;
  text-align: center;
  font-weight: 400;
  margin: 0.5rem;
  padding: 1rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: white;
  font-size: 2rem;
}

.active {
  visibility: visible;
}
@media only screen and (max-width: 75em) {
  figure {
    width: 90%;
    margin: auto;
  }
}

.rocks .navBtn {
  border: 1px solid var(--color-primary-3);
  border-radius: 50%;
  box-shadow: 2px 2px var(--color-primary-2);
  background-color: var(--color-primary-3);
}
</style>
